import { Injectable, inject, signal } from '@angular/core';
import { Auth, signOut, UserCredential, createUserWithEmailAndPassword, updateProfile, signInWithEmailAndPassword, user, sendPasswordResetEmail } from '@angular/fire/auth';
import { LanguageInterface, PMInterface, UserIDInterface, UserInterface } from '../interfaces';
import { DbService } from './db.service';
import { TranslocoService } from '@jsverse/transloco';


@Injectable({
  providedIn: 'root'
})

export class AuthService {
  firebaseAuth = inject(Auth);
  translocoService = inject(TranslocoService)
  db = inject(DbService);
  user$ = user(this.firebaseAuth);
  currentUserSig = signal<UserInterface | null | undefined>(undefined);
  currentUserIDSig = signal<UserIDInterface | null | undefined>(undefined);
  currentLangSig = signal<LanguageInterface | undefined>(undefined);

  constructor() {
    
    this.user$.subscribe(async firebaseUser => {
      const uid = firebaseUser?.uid ?? null;
      this.currentUserIDSig.set({ uid: uid });
      this.loadUserData(uid)
      if (!uid) return;
      const sessionLang = sessionStorage.getItem('userLanguage');
      if (sessionLang) {
        this.currentLangSig.set({ lang: sessionLang });
        this.translocoService.setActiveLang(sessionLang);
        return;
      }
      const localLang = localStorage.getItem('userLanguage');
      if (localLang) {
        this.currentLangSig.set({ lang: localLang });
        this.translocoService.setActiveLang(localLang);
        sessionStorage.setItem('userLanguage', localLang);
        return;
      }
      try {
        const firestoreLang = await this.db.getUserDocsAsync(this.currentUserIDSig().uid)
        if (firestoreLang && typeof firestoreLang['lang'] === 'string') {
          this.currentLangSig.set({ lang: firestoreLang['lang'] });
          this.translocoService.setActiveLang(firestoreLang['lang']);
          sessionStorage.setItem('userLanguage', firestoreLang['lang']);
        }
      } catch (err) {
        console.warn(err);
      }
    });
  }

  async RegisterWithEmail(email: string, password: string, username: string, language: string): Promise<void> {
    const response = await createUserWithEmailAndPassword(this.firebaseAuth, email, password)
    await updateProfile(response.user, { displayName: username })
    this.currentUserSig.set({
      uid: response.user.uid!,
      email: response.user.email!,
      name: username,
      lang: language
    });
  }

  async SignInWithEmailAsync(email: string, password: string): Promise<UserCredential> {
    const userData = await signInWithEmailAndPassword(this.firebaseAuth, email, password);
    this.currentUserIDSig.set({
      uid: userData.user.uid
    });
    return userData
  }

  async getGetAuth() {
    return this.firebaseAuth;
  }

  async signOutAsync(): Promise<void> {
    const auth = this.firebaseAuth;
    await signOut(auth);
    this.currentUserSig.set(null);
  }

  async ResetPasswordAsync(email: string): Promise<void> {
    await sendPasswordResetEmail(this.firebaseAuth, email);
  }

  async loadUserData(userUID: string): Promise<void> {
    const userData = await this.db.getUserDocsAsync(userUID)
    this.db.currentUserDataSig.set({
      currency: userData?.['currency'],
      email: userData?.['email'],
      lang: userData?.['lang'],
      name: userData?.['name'],
      newUser: userData?.['newUser'],
      username: userData?.['username'],
      plan: userData?.['plan'],
      regDate: userData?.['regDate'],
      surname: userData?.['surname'],
      transCounter: userData?.['transCounter'],
      uid: userData?.['uid']
    });
    const paymentMethods = await this.db.getPaymentMethodsAsync(userUID) as PMInterface;
    this.db.currentPaymentMethodsSig.set(paymentMethods)
  }
}
